import { ReactElement, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ROUTES } from '../../../helpers/constans';
import { setNewPasswordSchema } from '../../../helpers/formik-shemas/auth.schema';
import { fetchSetNewPassword } from '../../../services/auth.service';
import { getAuthErrorMessage } from '../../../helpers/auth-error';
import { IInterceptorError } from '../../../types/@interceptor';
import AuthLayout from '../components/auth-layout';
import AuthTextField from '../components/auth-text-field';
import { styles } from '../components/auth-layout.styles';

interface IFormValues {
  password: string;
  confirmPassword: string;
}

const SetNewPassword = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const codeParam = searchParams.get('code');

    if (!codeParam) {
      navigate(ROUTES.resetPassword);
      return;
    }

    setCode(codeParam);
  }, [searchParams, navigate]);

  const formik = useFormik<IFormValues>({
    initialValues: { password: '', confirmPassword: '' },
    validationSchema: setNewPasswordSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async ({ password }, helpers) => {
      if (!code) {
        setFeedback({ type: 'error', text: 'This reset link is invalid or has expired.' });
        return;
      }

      setIsLoading(true);
      setFeedback(null);

      try {
        const response = await fetchSetNewPassword({
          code,
          newPassword: password,
        });
        setIsSuccess(true);
        setFeedback({
          type: 'success',
          text: response.message || 'Your password has been updated. Redirecting to sign in…',
        });
        helpers.resetForm();
        window.setTimeout(() => {
          navigate(ROUTES.login);
        }, 2000);
      } catch (error) {
        setIsSuccess(false);
        setFeedback({
          type: 'error',
          text: getAuthErrorMessage(error as IInterceptorError),
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <AuthLayout
      title="Set a new password"
      subtitle="Choose a new password for your XDOS account. It must be at least 8 characters."
    >
      <Box
        component="form"
        sx={styles.form}
        onSubmit={formik.handleSubmit}
        noValidate
        aria-busy={isLoading}
      >
        {feedback ? (
          <Alert severity={feedback.type} sx={styles.alert} role="alert">
            {feedback.text}
          </Alert>
        ) : null}

        <AuthTextField
          name="password"
          label="New password"
          type="password"
          autoComplete="new-password"
          formik={formik}
          disabled={isLoading || isSuccess}
          icon={<LockOutlinedIcon fontSize="small" sx={styles.fieldIcon} />}
        />
        <AuthTextField
          name="confirmPassword"
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          formik={formik}
          disabled={isLoading || isSuccess}
          icon={<LockOutlinedIcon fontSize="small" sx={styles.fieldIcon} />}
        />

        <Button type="submit" sx={styles.submit} disabled={isLoading || isSuccess} fullWidth>
          {isLoading ? (
            <CircularProgress size={22} color="inherit" />
          ) : isSuccess ? (
            'Password updated'
          ) : (
            'Update password'
          )}
        </Button>
      </Box>

      <Typography sx={styles.footer}>
        Remember your password?{' '}
        <Typography component={Link} to={ROUTES.login} sx={styles.textLink}>
          Back to sign in
        </Typography>
      </Typography>
    </AuthLayout>
  );
};

export default SetNewPassword;
